/**
 * Утилиты для валидации UI Schema
 */

/**
 * Валидирует UI Schema на соответствие стандарту JSON Forms
 * @param {Object} uiSchema - UI схема для валидации
 * @param {Object} jsonSchema - соответствующая JSON схема для проверки совместимости
 * @returns {Object} - результат валидации { isValid: boolean, errors: Array }
 */
export const validateUiSchema = (uiSchema, jsonSchema = null) => {
  const errors = [];
  const warnings = [];

  if (!uiSchema || typeof uiSchema !== 'object') {
    return {
      isValid: false,
      errors: ['UI Schema должен быть объектом']
    };
  }

  // Проверка обязательных полей для корневого элемента
  if (!uiSchema.type) {
    errors.push('UI Schema должен содержать поле "type"');
  } else if (!['Group', 'HorizontalLayout', 'VerticalLayout', 'Control', 'Label'].includes(uiSchema.type)) {
    errors.push('Поле "type" должно содержать валидный тип: Group, HorizontalLayout, VerticalLayout, Control, Label');
  }

  // Проверка элементов в зависимости от типа
  if (uiSchema.type === 'Group') {
    if (!uiSchema.label || typeof uiSchema.label !== 'string') {
      errors.push('Элемент Group должен содержать поле "label" типа string');
    }
    if (!uiSchema.elements || !Array.isArray(uiSchema.elements)) {
      errors.push('Элемент Group должен содержать массив "elements"');
    }
  }

  if (uiSchema.type === 'Control') {
    if (!uiSchema.scope || typeof uiSchema.scope !== 'string') {
      errors.push('Элемент Control должен содержать поле "scope" типа string');
    } else if (!uiSchema.scope.startsWith('#/properties/')) {
      errors.push('Поле "scope" должно начинаться с "#/properties/"');
    }
    
    // Проверка соответствия scope с JSON Schema
    if (jsonSchema && jsonSchema.properties) {
      const fieldName = uiSchema.scope.replace('#/properties/', '');
      if (!jsonSchema.properties[fieldName]) {
        errors.push(`Поле "${fieldName}" из scope не найдено в JSON Schema properties`);
      }
    }
  }


  if (uiSchema.type === 'Label') {
    if (!uiSchema.text || typeof uiSchema.text !== 'string') {
      //alert(11111)
      warnings.push('Элемент Label должен содержать поле "text" типа string');
    }
  }

  // Рекурсивная валидация вложенных элементов
  if (uiSchema.elements && Array.isArray(uiSchema.elements)) {
    uiSchema.elements.forEach((element, index) => {
      const elementValidation = validateUiSchema(element, jsonSchema);
      if (!elementValidation.isValid) {
        elementValidation.errors.forEach(error => {
          errors.push(`Ошибка в элементе [${index}]: ${error}`);
        });
      }
      if (elementValidation.warnings.length > 0) {
        elementValidation.warnings.forEach(warning => {
          warnings.push(`Предупреждение в элементе [${index}]: ${warning}`);
        });
      }
    });
  }

  //console.log(warnings);

  return {
    isValid: errors.length === 0,
    errors,
    warnings
  };
};

/**
 * Валидирует соответствие UI Schema и JSON Schema
 * @param {Object} uiSchema - UI схема
 * @param {Object} jsonSchema - JSON схема
 * @returns {Object} - результат валидации { isValid: boolean, errors: Array }
 */
export const validateUiSchemaCompatibility = (uiSchema, jsonSchema) => {
  const errors = [];
  
  if (!jsonSchema || !jsonSchema.properties) {
    return {
      isValid: true,
      errors: []
    };
  }

  // Собираем все scope из UI Schema
  const scopes = [];
  const collectScopes = (element) => {
    if (element.scope) {
      scopes.push(element.scope);
    }
    if (element.elements) {
      element.elements.forEach(collectScopes);
    }
  };
  
  collectScopes(uiSchema);

  // Проверяем, что все scope соответствуют properties в JSON Schema
  for (const scope of scopes) {
    const fieldName = scope.replace('#/properties/', '');
    if (!jsonSchema.properties[fieldName]) {
      errors.push(`UI Schema ссылается на несуществующее поле "${fieldName}" в JSON Schema`);
    }
  }

  // Проверяем, что все properties из JSON Schema имеют соответствующие Control в UI Schema
  const jsonSchemaFields = Object.keys(jsonSchema.properties);
  const uiSchemaFields = scopes.map(scope => scope.replace('#/properties/', ''));
  
  for (const field of jsonSchemaFields) {
    if (!uiSchemaFields.includes(field)) {
      errors.push(`Поле "${field}" из JSON Schema не имеет соответствующего Control в UI Schema`);
    }
  }

  return {
    isValid: errors.length === 0,
    errors
  };
};

/**
 * Валидирует структуру UI Schema на наличие обязательных элементов
 * @param {Object} uiSchema - UI схема для валидации
 * @returns {Object} - результат валидации { isValid: boolean, errors: Array }
 */
export const validateUiSchemaStructure = (uiSchema) => {
  const errors = [];
  
  if (!uiSchema) {
    return {
      isValid: false,
      errors: ['UI Schema не может быть пустым']
    };
  }

  // Проверяем наличие элементов
  //if (!uiSchema.elements || !Array.isArray(uiSchema.elements) || uiSchema.elements.length === 0) {
  //  errors.push('UI Schema должен содержать хотя бы один элемент в массиве "elements"');
  //}

  // Проверяем, что все элементы имеют уникальные ID (если есть)
  const ids = [];
  const checkIds = (element) => {
    if (element.id) {
      if (ids.includes(element.id)) {
        errors.push(`Обнаружен дублирующийся ID: "${element.id}"`);
      } else {
        ids.push(element.id);
      }
    }
    if (element.elements) {
      element.elements.forEach(checkIds);
    }
  };
  
  checkIds(uiSchema);

  return {
    isValid: errors.length === 0,
    errors
  };
};

/**
 * Валидирует уникальность scope в UI Schema
 * @param {Object} uiSchema - UI схема для валидации
 * @returns {Object} - результат валидации { isValid: boolean, errors: Array }
 */
export const validateScopeUniqueness = (uiSchema) => {
  const errors = [];
  const scopes = [];
  
  // Собираем все scope из UI Schema
  const collectScopes = (element) => {
    if (element.scope) {
      scopes.push(element.scope);
    }
    if (element.elements) {
      element.elements.forEach(collectScopes);
    }
  };
  
  collectScopes(uiSchema);
  
  // Проверяем уникальность scope
  const duplicateScopes = [];
  const seenScopes = new Set();
  
  for (const scope of scopes) {
    if (seenScopes.has(scope)) {
      duplicateScopes.push(scope);
    } else {
      seenScopes.add(scope);
    }
  }
  
  if (duplicateScopes.length > 0) {
    errors.push(`Обнаружены дублирующиеся имена полей: ${duplicateScopes.join(', ')}`);
  }
  

  // Проверяем на похожие scope (различается только регистр)
  const lowerCaseScopes = scopes.map(scope => scope.toLowerCase());
  const uniqueLowerCaseScopes = new Set(lowerCaseScopes);
  if (lowerCaseScopes.length !== uniqueLowerCaseScopes.size) {
    const caseDuplicates = [];
    const seenLowerCase = new Set();
    for (const scope of scopes) {
      const lowerScope = scope.toLowerCase();
      if (seenLowerCase.has(lowerScope)) {
        caseDuplicates.push(scope);
      } else {
        seenLowerCase.add(lowerScope);
      }
    }
    errors.push(`Обнаружены scope с похожими названиями (различается только регистр): ${caseDuplicates.join(', ')}`);
  }
  
  return {
    isValid: errors.length === 0,
    errors
  };
};

/**
 * Комплексная валидация UI Schema
 * @param {Object} uiSchema - UI схема для валидации
 * @param {Object} jsonSchema - соответствующая JSON схема
 * @returns {Object} - результат валидации { isValid: boolean, errors: Array, warnings: Array }
 */
export const validateUiSchemaComprehensive = (uiSchema, jsonSchema = null) => {
  const allErrors = [];
  const allWarnings = [];
  
  // Основная валидация UI Schema
  const basicValidation = validateUiSchema(uiSchema, jsonSchema);
  allErrors.push(...basicValidation.errors);
  allWarnings.push(...basicValidation.warnings);

  //console.log(basicValidation)
  //alert(basicValidation.warnings.length)

  // Валидация структуры
  const structureValidation = validateUiSchemaStructure(uiSchema);
  allErrors.push(...structureValidation.errors);
  
  // Валидация совместимости с JSON Schema
  if (jsonSchema) {
    const compatibilityValidation = validateUiSchemaCompatibility(uiSchema, jsonSchema);
    allErrors.push(...compatibilityValidation.errors);
  }
  
  // Валидация уникальности scope
  const scopeUniquenessValidation = validateScopeUniqueness(uiSchema);
  allErrors.push(...scopeUniquenessValidation.errors);
  
  // Дополнительные проверки для предупреждений
  if (uiSchema.elements) {
    
    // Проверка на пустые группы
    const emptyGroups = uiSchema.elements.filter(el => 
      el.type === 'Group' && (!el.elements || el.elements.length === 0)
    );
    if (emptyGroups.length > 0) {
      allWarnings.push(`Обнаружено ${emptyGroups.length} пустых групп`);
    }
  }
  
  return {
    isValid: allErrors.length === 0,
    errors: allErrors,
    warnings: allWarnings
  };
};
